/**
 * Blood pooling + tracked trails.
 *
 * Every tile has a `blood` byte in [0, 255] representing pooled blood. When
 * an entity is wounded/crippled/killed we drop a pool at its tile. Bleeding
 * entities drip per tick. Any entity walking over a bloody tile picks blood
 * up on their `bloodOnFeet` counter, then deposits diminishing amounts at
 * subsequent tiles → visible trail.
 *
 * Renderer paints a dark-red overlay per tile scaled by the blood level.
 *
 * Headless — no PixiJS.
 */

import type { BodyState, Severity } from './body';
import { tileAt, type MapData } from './map';
import { TileType } from './tiles';

export class BloodMap {
  readonly blood: Uint8Array;
  /** World tick at which each tile last received blood — the trail's freshness
   *  gradient. Guards follow *increasing* age toward the bleeder's current
   *  position (see `sim/bloodTrail.ts`). 0 = never blooded. */
  readonly age: Uint32Array;
  readonly width: number;
  readonly height: number;
  /** Current world tick, stamped into `age` on every `add`. Set once per sim
   *  tick by the caller before any blood is deposited. */
  tick = 0;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.blood = new Uint8Array(width * height);
    this.age = new Uint32Array(width * height);
  }

  at(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return 0;
    return this.blood[y * this.width + x] ?? 0;
  }

  /** World tick a tile was last blooded (0 = never). Higher = fresher. */
  ageAt(x: number, y: number): number {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return 0;
    return this.age[y * this.width + x] ?? 0;
  }

  add(x: number, y: number, amount: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    if (amount <= 0) return;
    const idx = y * this.width + x;
    const next = Math.min(255, (this.blood[idx] ?? 0) + amount);
    this.blood[idx] = next;
    this.age[idx] = this.tick;
  }

  /** Blood dries slowly over time — 1 pt per second per tile. */
  tickDry(tickDt: number, dryPerSecPerTile = 1): void {
    const dec = dryPerSecPerTile * tickDt;
    // Since blood is a Uint8, accumulate a fractional debt globally and
    // apply as integer subtractions.
    this.dryAccum += dec;
    if (this.dryAccum >= 1) {
      const whole = Math.floor(this.dryAccum);
      this.dryAccum -= whole;
      for (let i = 0; i < this.blood.length; i++) {
        const v = this.blood[i] ?? 0;
        this.blood[i] = v > whole ? v - whole : 0;
      }
    }
  }
  private dryAccum = 0;

  /** Wipe a single tile clean (janitor mopping). */
  clearTile(x: number, y: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    const idx = y * this.width + x;
    this.blood[idx] = 0;
    this.age[idx] = 0;
  }

  reset(): void {
    this.blood.fill(0);
    this.age.fill(0);
    this.dryAccum = 0;
  }
}

/** Amount of blood dropped at a tile for a given hit severity. */
export function bloodFromSeverity(sev: Severity): number {
  switch (sev) {
    case 'graze':
      return 8;
    case 'wound':
      return 40;
    case 'cripple':
      return 110;
    case 'kill':
      return 200;
  }
}

/** Passive bleed drip: proportional to bleedPerSec. A corpse (or the death
 *  ghost, which shares the same body) does NOT keep bleeding — mirrors the
 *  `body.dead` guard in `tickBleedDamage`, so a free-flying ghost leaves no
 *  trail of fresh blood behind it. */
export function bleedTickAmount(body: BodyState, tickDt: number): number {
  if (body.dead || body.bleedPerSec <= 0) return 0;
  return body.bleedPerSec * tickDt * 4; // scale so bleedPerSec=5 drops ~20/tick*20 across ticks
}

/**
 * Update per-entity foot blood when they arrive on a new tile.
 * `stepInto` is called when moveProgress just snapped to 0 with a new (x, y).
 * If the tile has blood, the entity picks up half; if the entity has blood
 * on their feet, they leave a smaller amount here.
 */
export interface BloodBearer {
  bloodOnFeet: number;
}

export function stepIntoTile(bearer: BloodBearer, bmap: BloodMap, x: number, y: number, map: MapData): void {
  // Void tiles hold no blood.
  if (tileAt(map, x, y) === TileType.Void) return;
  const here = bmap.at(x, y);
  if (here > 0) {
    // Pick up half. Cap on-feet at 200.
    const pickup = Math.min(200, Math.floor(here / 2));
    bearer.bloodOnFeet = Math.min(200, bearer.bloodOnFeet + pickup);
  }
  if (bearer.bloodOnFeet > 0) {
    // Deposit a fraction of what's on the feet.
    const drop = Math.min(20, Math.max(2, Math.floor(bearer.bloodOnFeet / 8)));
    bmap.add(x, y, drop);
    bearer.bloodOnFeet = Math.max(0, bearer.bloodOnFeet - drop);
  }
}
